import {
  Box,
  Button,
  Divider,
  Grid,
  Typography,
  CircularProgress,
} from "@mui/material";
import React, { useEffect } from "react";
import ProductCard from "../components/ProductCard";
import { useQuery } from "@tanstack/react-query";
import { fetchProducts } from "../services/productService";

const Products = () => {
  const scrollToCategory = (category) => {
    const element = document.getElementById(category);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 160,
        behavior: "smooth",
      });
    }
  };

  /**
   * Custom fetch function with additional debugging
   */
  const fetchProductsWithDebug = async () => {
    try {
      // Log the API URL being used
      // Make the API call
      const products = await fetchProducts();
      
      // Log the response
      if (products.length > 0) {
        }
      
      return products;
    } catch (error) {
      console.error('Error in fetchProductsWithDebug:', error);
      throw error;
    }
  };

  /**
   * React Query Hook for Products
   *
   * This hook fetches all products and caches them for 5 minutes.
   * It replaces the previous implementation that made duplicate API calls.
   *
   * Benefits:
   * - Automatic deduplication of identical requests
   * - Built-in loading and error states
   * - Consistent caching across components
   *
   * See docs/react-query-implementation.md for more details
   */
  const {
    data: products,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["products"], // Unique identifier for this query in the cache
    queryFn: fetchProductsWithDebug, // Using our debug version
    staleTime: 5 * 60 * 1000, // Data considered fresh for 5 minutes
  });

  /**
   * Group products by category using memoization
   *
   * This useMemo hook processes the products data once it's available
   * and organizes it by category for display. The memoization ensures
   * this computation only runs when the products data changes.
   *
   * Updated to work with Supabase data structure where category is a relationship
   */
  const productsByCategory = React.useMemo(() => {
    if (!products || !Array.isArray(products) || products.length === 0) {
      return {};
    }
    
    return products.reduce((acc, product) => {
      // Ensure product is an object with required properties
      if (!product || typeof product !== 'object') {
        return acc;
      }

      // Derive group from categories array; uncategorized products fall under 'Other'.
      const groupName = (p) => (p.categories && p.categories[0]) || 'Other';
      const displayCategory = groupName(product);

      if (!acc[displayCategory]) {
        acc[displayCategory] = [];
      }
      acc[displayCategory].push(product);

      return acc;
    }, {});
  }, [products]);

  // Debug the grouped products
  React.useEffect(() => {
    console.log('Products by category:', productsByCategory);
  }, [productsByCategory]);

  // Update meta tags directly when component mounts
  useEffect(() => {
    // Only update meta tags if we have products data
    if (!isLoading && products) {
      // Update meta tags for SEO
      updateMetaTags({
        title: "Handcrafted Leather Products | KBros Leather",
        description:
          "Discover our collection of handcrafted leather wallets, keychains, and accessories. Each piece is made with premium materials and customizable with personalized engravings.",
        keywords:
          "leather wallets, leather keychains, handcrafted leather, personalized leather gifts, custom engraving",
        url: `${window.location.origin}/products`,
        structuredData: generateStructuredData(),
      });
    }
  }, [products, isLoading]);

  if (isLoading) {
    return (
      <Box
        sx={{
          padding: 4,
          backgroundColor: "#f5f5f5",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading products...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ padding: 4, backgroundColor: "#f5f5f5" }}>
        <Typography variant="h5" color="error" gutterBottom align="center">
          {error instanceof Error
            ? error.message
            : "Failed to load products. Please try again later."}
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </Box>
      </Box>
    );
  }

  // Generate structured data for product listing
  const generateStructuredData = () => {
    const itemListElements = [];
    let position = 1;

    // Add each category's products to the list
    Object.keys(productsByCategory).sort((a, b) => a === 'Other' ? 1 : b === 'Other' ? -1 : a.localeCompare(b)).forEach((category) => {
      productsByCategory[category]?.forEach((product) => {
        // Get primary image from Supabase images relationship
        const primaryImage = product.images?.find(img => img.is_primary) || product.images?.[0];
        const imageUrl = primaryImage?.url || "";

        itemListElements.push({
          "@type": "ListItem",
          position: position++,
          item: {
            "@type": "Product",
            name: product.name,
            description: product.short_description || product.description || "",
            image: imageUrl,
            url: `${window.location.origin}/products/${product.slug || product.id}`,
            offers: {
              "@type": "Offer",
              price: product.price,
              priceCurrency: "USD",
              availability:
                product.inventory_quantity > 0
                  ? "https://schema.org/InStock"
                  : "https://schema.org/OutOfStock",
            },
          },
        });
      });
    });

    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: itemListElements,
    };
  };

  // Function to directly update meta tags
  const updateMetaTags = (metaData) => {
    // Update title
    document.title = metaData.title;

    // Update description
    let descriptionTag = document.querySelector('meta[name="description"]');
    if (descriptionTag) {
      descriptionTag.setAttribute("content", metaData.description);
    } else {
      descriptionTag = document.createElement("meta");
      descriptionTag.setAttribute("name", "description");
      descriptionTag.setAttribute("content", metaData.description);
      document.head.appendChild(descriptionTag);
    }

    // Update keywords
    let keywordsTag = document.querySelector('meta[name="keywords"]');
    if (keywordsTag) {
      keywordsTag.setAttribute("content", metaData.keywords);
    } else {
      keywordsTag = document.createElement("meta");
      keywordsTag.setAttribute("name", "keywords");
      keywordsTag.setAttribute("content", metaData.keywords);
      document.head.appendChild(keywordsTag);
    }

    // Update canonical link
    let canonicalTag = document.querySelector('link[rel="canonical"]');
    if (canonicalTag) {
      canonicalTag.setAttribute("href", metaData.url);
    } else {
      canonicalTag = document.createElement("link");
      canonicalTag.setAttribute("rel", "canonical");
      canonicalTag.setAttribute("href", metaData.url);
      document.head.appendChild(canonicalTag);
    }

    // Update Open Graph tags
    updateOpenGraphTag("og:title", metaData.title);
    updateOpenGraphTag("og:description", metaData.description);
    updateOpenGraphTag("og:type", "website");
    updateOpenGraphTag("og:url", window.location.href);
    updateOpenGraphTag("og:site_name", "KBros Leather");

    // Update structured data
    if (metaData.structuredData) {
      let scriptTag = document.querySelector(
        'script[type="application/ld+json"]',
      );
      if (scriptTag) {
        scriptTag.textContent = JSON.stringify(metaData.structuredData);
      } else {
        scriptTag = document.createElement("script");
        scriptTag.setAttribute("type", "application/ld+json");
        scriptTag.textContent = JSON.stringify(metaData.structuredData);
        document.head.appendChild(scriptTag);
      }
    }
  };

  // Helper function to update Open Graph tags
  const updateOpenGraphTag = (property, content) => {
    let tag = document.querySelector(`meta[property="${property}"]`);
    if (tag) {
      tag.setAttribute("content", content);
    } else {
      tag = document.createElement("meta");
      tag.setAttribute("property", property);
      tag.setAttribute("content", content);
      document.head.appendChild(tag);
    }
  };

  return (
    <Box sx={{ padding: 4, backgroundColor: "#f5f5f5" }}>
      {/* SEO Meta Tags are now handled directly via DOM manipulation */}
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        align="center"
        sx={{ marginBottom: 4 }}
      >
        Our Products
      </Typography>

      <Box
        sx={{
          position: "sticky",
          top: 60,
          zIndex: 1000,
          backgroundColor: "#ffffff",
          padding: 2,
          boxShadow: 3,
          marginBottom: 4,
        }}
      >
        <Grid container spacing={2} justifyContent="center">
          {Object.keys(productsByCategory).sort((a, b) => a === 'Other' ? 1 : b === 'Other' ? -1 : a.localeCompare(b)).map((category) => (
            <Grid item key={category}>
              <Button
                variant="text"
                onClick={() => scrollToCategory(category)}
                sx={{ textTransform: "none" }}
                data-testid={`category-${category}`}
              >
                {category}
              </Button>
            </Grid>
          ))}
        </Grid>
      </Box>

      {Object.keys(productsByCategory).sort((a, b) => a === 'Other' ? 1 : b === 'Other' ? -1 : a.localeCompare(b)).map((category) => (
          <Box id={category} key={category} sx={{ marginBottom: 6 }}>
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{ fontWeight: "bold", color: "#333", letterSpacing: 1 }}
            >
              {category}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={4}>
              {productsByCategory[category]?.map((product) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                  <ProductCard product={product} />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
    </Box>
  );
};

export default Products;
